import { createRoute } from '@tanstack/react-router';
import { z } from 'zod/v3';

import { CountryCode } from 'common/api-types/common';
import { menuLayoutRoute } from 'root/MenuLayout';
import RequireSingleCountry from 'root/RequireSingleCountry';

import {
  productCreationReviewRouteSearchSchema,
  outerCasesIndexRouteSearchSchema,
  productCatalogMainRouteSearchSchema,
  productDetailsRouteSearchSchema,
  productFamiliesMainRouteSearchSchema,
  productSearchRouteSearchSchema,
  reviewRouteSearchSchema,
} from './search';
import {
  RedirectProductWithoutCountry,
  RedirectProductWithoutLanguage,
} from '../RedirectToProductDetails';

const productCatalogMainRoute = createRoute({
  getParentRoute: () => menuLayoutRoute,
  path: 'catalog',
  validateSearch: productCatalogMainRouteSearchSchema,
}).lazy(() => import('../ProductCatalog').then((d) => d.Route));

const productSearchRoute = createRoute({
  getParentRoute: () => productCatalogMainRoute,
  path: '/',
  validateSearch: productSearchRouteSearchSchema,
}).lazy(() => import('../ProductSearchView/ProductSearchView').then((d) => d.Route));

const productWithoutCountryRoute = createRoute({
  component: RedirectProductWithoutCountry,
  getParentRoute: () => productCatalogMainRoute,
  path: '$productId',
  params: z.object({ productId: z.string() }),
});

const productWithoutLanguageRoute = createRoute({
  component: RedirectProductWithoutLanguage,
  getParentRoute: () => productCatalogMainRoute,
  path: '$productId/country/$countryCode',
  params: z.object({
    productId: z.string(),
    countryCode: z.custom<CountryCode>(),
  }),
});

const productDetailsRoute = createRoute({
  getParentRoute: () => productCatalogMainRoute,
  path: '$productId/country/$countryCode/lang/$languageCode',
  params: z.object({
    productId: z.string(),
    countryCode: z.custom<CountryCode>(),
    languageCode: z.string(),
  }),
  validateSearch: productDetailsRouteSearchSchema,
}).lazy(() => import('../ProductDetailsView/ProductDetailsView').then((d) => d.Route));

const outerCasesRoute = createRoute({
  getParentRoute: () => productCatalogMainRoute,
  path: 'outer-cases',
});

const outerCasesIndexRoute = createRoute({
  getParentRoute: () => outerCasesRoute,
  path: '/',
  validateSearch: outerCasesIndexRouteSearchSchema,
}).lazy(() => import('../ProductSearchView/OuterCases/OuterCases').then((d) => d.Route));

const productCreationReviewMainRoute = createRoute({
  getParentRoute: () => productCatalogMainRoute,
  path: 'product-creation-review',
  component: RequireSingleCountry,
});

const productCreationReviewRoute = createRoute({
  getParentRoute: () => productCreationReviewMainRoute,
  path: '/',
  validateSearch: productCreationReviewRouteSearchSchema,
}).lazy(() =>
  import('../ProductCreationReviewView/ProductCreationReviewView').then((d) => d.Route),
);

const reviewRoute = createRoute({
  getParentRoute: () => productCatalogMainRoute,
  path: 'review',
  validateSearch: reviewRouteSearchSchema,
}).lazy(() => import('../ReviewView/ReviewView').then((d) => d.Route));

const reviewIndexRoute = createRoute({
  getParentRoute: () => reviewRoute,
  path: '/',
}).lazy(() => import('../ReviewView/ReviewPendingView').then((d) => d.Route));

const reviewPreviousRoute = createRoute({
  getParentRoute: () => reviewRoute,
  path: 'previous',
}).lazy(() => import('../ReviewView/ReviewPreviousView').then((d) => d.Route));

const merchantCatalogMainRoute = createRoute({
  getParentRoute: () => menuLayoutRoute,
  path: 'merchant-catalog',
}).lazy(() => import('../MerchantCatalog').then((d) => d.Route));

const merchantProductSearchRoute = createRoute({
  getParentRoute: () => merchantCatalogMainRoute,
  path: '/',
}).lazy(() => import('../MerchantProductView/MerchantProductSearchView').then((d) => d.Route));

export const productCatalogRouteTree = productCatalogMainRoute.addChildren({
  productSearchRoute,
  productWithoutCountryRoute,
  productWithoutLanguageRoute,
  productDetailsRoute,
  outerCasesRoute: outerCasesRoute.addChildren({ outerCasesIndexRoute }),
  productCreationReviewMainRoute: productCreationReviewMainRoute.addChildren({
    productCreationReviewRoute,
  }),
  reviewRoute: reviewRoute.addChildren({ reviewIndexRoute, reviewPreviousRoute }),
});

export const productFamiliesMainRoute = createRoute({
  getParentRoute: () => menuLayoutRoute,
  path: 'product-families',
  validateSearch: productFamiliesMainRouteSearchSchema,
}).lazy(() => import('../ProductFamiliesView/ProductFamiliesView').then((d) => d.Route));

export const merchantProductCatalogRouteTree = merchantCatalogMainRoute.addChildren({
  merchantProductSearchRoute,
});
